import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { 
  Box, Card, CardContent, CardMedia, Typography, 
  Button, CircularProgress, List, ListItem, ListItemText, 
  Grid, Chip
} from "@mui/material";
import {  ShoppingCart, Download } from "@mui/icons-material";

const categoryMap = {
  courses: "CoursesProducts",
  books: "BooksProducts",
  entertainment: "EntertainmentProducts",
  audiobooks: "AudioProducts",
  anime: "AnimeProducts",
  software: "SoftwareProducts",
  games: "GamesProducts",
  tools: "ToolsProducts",
  wallpapers: "Wallpaper",
  islamic: "IslamicDB",
  english: "EnglishProducts",
};

const WHATSAPP_NUMBER = "+923455861630";

const CategoryPage = () => {
  const { category } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Your original data fetching logic
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      const fileName = categoryMap[category.toLowerCase()];

      if (!fileName) {
        console.error("Category not found:", category);
        setProducts([]);
        setLoading(false);
        return;
      }

      try {
        const module = await import(`../database/${fileName}.js`);
        setProducts(module.default);
      } catch (error) {
        console.error("Error loading category data:", error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [category]);

  // Your original WhatsApp handler
  const handleBuyNow = (product) => {
    const message = encodeURIComponent(
      `Hello, I am interested in buying:\n\n📌 *${product.name}*\n💰 *Price:* ${product.price || "N/A"}\n📎 *Product Link:* ${product.productLink}\n\nPlease provide more details.`
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, "_blank");
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh' 
      }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h3" sx={{ 
        mb: 4, 
        fontWeight: 700,
        color: 'text.primary',
        textAlign: 'center',
        textTransform: 'capitalize',
        position: 'relative',
        '&:after': {
          content: '""',
          position: 'absolute',
          bottom: -8,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 120,
          height: 4,
          backgroundColor: 'primary.main',
          borderRadius: 2
        }
      }}>
        {category} Collection
      </Typography>

      <Grid container spacing={3}>
        {products.length > 0 ? (
          products.map((product) => (
            <Grid item xs={12} sm={6} md={4} key={product.id}>
              <Card sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                boxShadow: 3,
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-5px)'
                }
              }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={product.image}
                  alt={product.name}
                  sx={{ objectFit: 'cover' }}
                />

                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={category}
                      color="primary"
                      size="small"
                      sx={{ mb: 1, fontWeight: 600 }}
                    />
                    <Typography variant="h6" sx={{ 
                      fontWeight: 700,
                      color: 'text.primary'
                    }}>
                      {product.name}
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ 
                    mb: 2,
                    color: 'text.secondary',
                    minHeight: 60
                  }}>
                    {product.description}
                  </Typography>

                  {category === "tools" && (
                    <Typography variant="subtitle1" sx={{ 
                      fontWeight: 700,
                      color: 'secondary.main',
                      mb: 2
                    }}>
                      Price: {product.price}
                    </Typography>
                  )}

                  {category === "courses" && product.courseList?.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Included Courses:
                      </Typography>
                      <List dense>
                        {product.courseList.map((course, index) => (
                          <ListItem key={index} sx={{ py: 0.5 }}>
                            <ListItemText 
                              primary={course} 
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    mt: 'auto',
                    '& .MuiButton-root': {
                      flex: 1,
                      py: 1,
                      fontWeight: 600,
                      borderRadius: 1
                    }
                  }}>
                    {category === "tools" ? (
                      <>
                        <Button
                          variant="outlined"
                          href={product.productLink}
                          target="_blank"
                        >
                          Preview
                        </Button>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<ShoppingCart />}
                          onClick={() => handleBuyNow(product)}
                        >
                          Buy Now
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Download />}
                        href={product.productLink}
                        target="_blank"
                      >
                        Get Link
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Box sx={{ 
            width: '100%', 
            textAlign: 'center', 
            p: 4,
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2
          }}>
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              No products available in this category
            </Typography>
          </Box>
        )}
      </Grid>
    </Box>
  );
};

export default CategoryPage;